import { useEffect, useState } from 'react';

import type { EventBusEvent } from '../enums/EventBus';
import eventBus from '../event-bus/EventBus';

interface SubscriptonCallbackOpts {
  latestEmitted?: boolean;
  isDisabled?: boolean;
}

interface SubscriptionOpts<T> extends SubscriptonCallbackOpts {
  initialValue?: T;
}

export const useSubscription = <T>(
  event: EventBusEvent,
  options?: SubscriptionOpts<T>
): [T | null] => {
  const [state, setState] = useState<T | null>(options?.initialValue ?? null);

  useEffect(() => {
    if (options?.isDisabled) {
      return;
    }

    eventBus.on(event, setState);

    if (options?.latestEmitted) {
      const latestEmitted = eventBus.latestEmitted<T>(event);

      if (latestEmitted) {
        setState(latestEmitted);
      }
    }

    return () => eventBus.off(event, setState);
  }, [event, options?.isDisabled]);

  return [state];
};

export const useSubscriptionCallback = <T>(
  event: EventBusEvent,
  callback: (t: T) => void,
  options?: SubscriptonCallbackOpts
) => {
  useEffect(() => {
    if (options?.isDisabled) {
      return;
    }

    eventBus.on(event, callback);

    if (options?.latestEmitted) {
      const latestEmitted = eventBus.latestEmitted<T>(event);

      if (latestEmitted) {
        callback(latestEmitted);
      }
    }

    return () => eventBus.off(event, callback);
  }, [event, callback]);
};
